import React, {PureComponent} from 'react';
import {UserContext} from "../../core/UserContext";
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import withStyles from '@mui/styles/withStyles';
import Container from '@mui/material/Container';
import CircularProgress from "@mui/material/CircularProgress";
import {Regexer} from "../../core/auth/Regexer";
import {PageDataContext} from "../../core/PageDataContext";
import {Divider, IconButton, InputAdornment} from "@mui/material";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import {withRouter} from "../../hooks/withRouter";
import {Link as RouterLink} from "react-router-dom";
import {UrlBuilder} from "../../core/url/UrlBuilder";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import GoogleIcon from "@mui/icons-material/Google";
import AppleIcon from "@mui/icons-material/Apple";
import PersonIcon from "@mui/icons-material/Person";
import moment from "moment";
import WIPIcon from "../../res/img/icon.png";

const useStyles = theme => ({
	form: {
		width: '100%',
		marginTop: theme.spacing(1),
	},
	submit: {
		margin: theme.spacing(3, 0, 2),
	},
});

class Signin extends PureComponent {

	constructor(props) {
		super(props);
		this.state = {
			isLoading: false,
			isUserNotFound: false,
			isPasswordIncorrect: false,
			showPassword: false,
			isSigningUpAsGuest: false,
		};

		this.updateInputValue = this.updateInputValue.bind(this);
		this.submit = this.submit.bind(this);
	}

	componentDidMount() {
		this.context.setPageData({
			title: "Sign In to My Work in Progress"
		});
	}

	updateInputValue(event) {
		this.setState({
			[event.target.name]: event.target.value,
			isUserNotFound: false,
			isPasswordIncorrect: false,
		});
	}

	redirectToGoogleAuth = () => {
		window.location.href = '/api/users/google-signin';
	}

	redirectToAppleAuth = () => {
		window.location = '/api/users/apple-signin';
	}

	async submit(event, setUser) {
		event.preventDefault();

		this.setState({
			isLoading: true
		});

		let signinUser = {
			email: this.state.email,
			password: this.state.password,
			keepSignedIn: this.state.rememberMe
		};

		let response = await fetch('/api/users/signin', {
			method: 'POST',
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(signinUser)
		});

		let jsonResponse = await response.json();

		if (response.status === 200) {
			setUser(jsonResponse['data']);
			this.props.navigate('/');
		} else if (jsonResponse.message === "user_not_found") {
			this.setState({
				isUserNotFound: true,
				isPasswordIncorrect: false,
				isLoading: false,
			})
		} else if (jsonResponse.message === "incorrect_password") {
			this.setState({
				isPasswordIncorrect: true,
				isUserNotFound: false,
				isLoading: false,
			})
		} else {
			this.setState({
				isLoading: false,
			})
		}
	}

	showPassword = () => {
		this.setState({
			showPassword: !this.state.showPassword,
		});
	}

	signUpAsGuest = async (event, setUser) => {
		event.preventDefault();

		this.setState({
			isSigningUpAsGuest: true
		});

		let guestUser = {
			dateCreated: moment().format('L, h:mm:ss A'),
		};

		let response = await fetch(UrlBuilder.user.SignUpAsGuestApi(), {
			method: 'POST',
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(guestUser)
		});

		let jsonResponse = await response.json();

		if (response.status === 200) {
			setUser(jsonResponse['data']);
			this.props.navigate('/');
		}

		this.setState({
			isSigningUpAsGuest: false,
		})
	}

	render() {
		const {classes} = this.props;

		let isEmailInvalid = false;
		let emailFieldHelperText = null;
		if (this.state.email && !Regexer.testEmail(this.state.email)) {
			isEmailInvalid = true;
			emailFieldHelperText = 'Please enter a valid email'
		} else if (this.state.isUserNotFound) {
			isEmailInvalid = true;
			emailFieldHelperText = 'Email not found'
		}

		let isPasswordInvalid = false;
		let passwordFieldHelperText = null;
		if (this.state.isPasswordIncorrect) {
			isPasswordInvalid = true;
			passwordFieldHelperText = 'Password is incorrect'
		}

		let isSubmitDisabled = !this.state.email || !Regexer.testEmail(this.state.email) || !this.state.password;

		return (
            <Container component="main" maxWidth="xs">
				<CssBaseline/>
				<Box sx={{
					mt: 4,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
				}}>
					<Box sx={{
						width: '100%',
						p: 3,
						borderRadius: 3,
						border: '1px solid',
						borderColor: 'divider',
						bgcolor: 'background.paper',
					}}>
						<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
							<img src={WIPIcon} width={48} alt="WIP" style={{ borderRadius: 12, marginBottom: 12 }} />
							<Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
								Sign in
							</Typography>
							<Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
								Welcome back
							</Typography>
						</Box>

						<Grid container direction='column' display="flex" justifyContent="center" alignItems="center" spacing={1} sx={{ mb: 2 }}>
							<Grid item>
								<Button sx={{ width: 280, height: 44 }} variant='contained'
								        onClick={this.redirectToGoogleAuth} size='medium'
								        startIcon={<GoogleIcon />}>
									Continue with Google
								</Button>
							</Grid>
							<Grid item>
								<Button sx={{ width: 280, height: 44 }} variant='contained' onClick={this.redirectToAppleAuth} size='medium' startIcon={<AppleIcon />}>
									Continue with Apple
								</Button>
							</Grid>
						</Grid>

						<Divider sx={{ my: 2 }}>
							<Typography variant="caption" sx={{ color: 'text.secondary' }}>or with email</Typography>
						</Divider>

						<form className={classes.form} noValidate>
							<TextField
								variant="outlined"
								margin="normal"
								required
								fullWidth
								size="small"
								id="email"
								label="Email Address"
								name="email"
								autoComplete="email"
								onChange={this.updateInputValue}
								error={isEmailInvalid}
								helperText={emailFieldHelperText}
							/>
							<TextField
								InputProps={{
									endAdornment:
										<InputAdornment position="end">
											<IconButton onClick={this.showPassword} edge="end" size="small">
												{this.state.showPassword ? <Visibility /> : <VisibilityOff />}
											</IconButton>
										</InputAdornment>
								}}
								variant="outlined"
								margin="normal"
								required
								fullWidth
								size="small"
								name="password"
								label="Password"
								type={this.state.showPassword ? "text" : "password"}
								id="password"
								autoComplete="current-password"
								onChange={this.updateInputValue}
								error={isPasswordInvalid}
								helperText={passwordFieldHelperText}
							/>
							<UserContext.Consumer>
								{({user, setUser}) => (
									<Button
										type="submit"
										fullWidth
										variant="contained"
										color="primary"
										sx={{ mt: 2, mb: 1, height: 44 }}
										onClick={e => this.submit(e, setUser)}
										disabled={isSubmitDisabled}
									>
										{
											this.state.isLoading ?
												<div>
													<CircularProgress size="1rem" color="inherit" /> Sign in
												</div> :
												<div>
													Sign in
												</div>
										}
									</Button>
								)}
							</UserContext.Consumer>
						</form>
					</Box>

					<Box sx={{ mt: 2, textAlign: 'center' }}>
						<Typography variant="body2" sx={{ color: 'text.secondary' }}>
							Don't have an account?{' '}
							<Link component={RouterLink} to={UrlBuilder.user.SignUpPage()} sx={{ color: 'secondary.main', fontWeight: 600 }}>
								Sign Up
							</Link>
						</Typography>
					</Box>

					<Box sx={{ mt: 1 }}>
						<UserContext.Consumer>
							{({user, setUser}) => (
								<Button
									type="submit"
									startIcon={this.state.isSigningUpAsGuest ? <CircularProgress size="1rem" color="inherit" /> : <PersonIcon/>}
									variant="text"
									size="small"
									color="primary"
									sx={{ fontSize: 13, textTransform: 'none' }}
									onClick={e => this.signUpAsGuest(e, setUser)}
									disabled={this.state.isSigningUpAsGuest}
								>
									Try as Guest
								</Button>
							)}
						</UserContext.Consumer>
					</Box>

					<Box mt={4}>
						<Typography variant="body2" color="textSecondary" align="center">
							{'Copyright © My Work in Progress '}
							{new Date().getFullYear()}{'. '}
							<Link component={RouterLink} to={UrlBuilder.user.privacyPage()} sx={{ color: 'text.secondary' }}>
								Privacy Policy
							</Link>
						</Typography>
					</Box>
				</Box>
			</Container>
        );
	}
}

Signin.contextType = PageDataContext;

export default withStyles(useStyles)(withRouter(Signin));
